import { useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { cn } from '@/lib/utils';

type CalcState = {
  display: string;
  prev: number | null;
  op: string | null;
  waitingForOperand: boolean;
};

const initialState: CalcState = {
  display: '0',
  prev: null,
  op: null,
  waitingForOperand: false,
};

function calculate(a: number, op: string, b: number): number {
  switch (op) {
    case '+': return a + b;
    case '−': return a - b;
    case '×': return a * b;
    case '÷': return b !== 0 ? a / b : NaN;
    default: return b;
  }
}

function formatDisplay(val: string): string {
  if (val === 'Error') return 'Error';
  const num = parseFloat(val);
  if (isNaN(num)) return 'Error';
  if (Math.abs(num) > 1e15 || (Math.abs(num) < 1e-9 && num !== 0)) {
    return num.toExponential(6).replace(/\.?0+e/, 'e');
  }
  // Limit displayed length
  if (val.length > 12) {
    const rounded = parseFloat(num.toPrecision(10)).toString();
    return rounded;
  }
  return val;
}

const Index = () => {
  useSeoMeta({
    title: 'Calcly — Calculator',
    description: 'A clean, modern calculator.',
  });

  const [state, setState] = useState<CalcState>(initialState);

  const handleDigit = (digit: string) => {
    setState((s) => {
      if (s.waitingForOperand) {
        return { ...s, display: digit, waitingForOperand: false };
      }
      if (s.display === '0' && digit !== '.') return { ...s, display: digit };
      if (digit === '.' && s.display.includes('.')) return s;
      if (s.display.replace(/[^0-9]/g, '').length >= 12) return s;
      return { ...s, display: s.display + digit };
    });
  };

  const handleOperator = (op: string) => {
    setState((s) => {
      const current = parseFloat(s.display);
      if (s.prev !== null && s.op && !s.waitingForOperand) {
        const result = calculate(s.prev, s.op, current);
        const display = isNaN(result) ? 'Error' : String(result);
        return { display, prev: isNaN(result) ? null : result, op, waitingForOperand: true };
      }
      return { ...s, prev: current, op, waitingForOperand: true };
    });
  };

  const handleEquals = () => {
    setState((s) => {
      if (s.prev === null || s.op === null) return s;
      const current = parseFloat(s.display);
      const result = calculate(s.prev, s.op, current);
      const display = isNaN(result) ? 'Error' : String(result);
      return { display, prev: null, op: null, waitingForOperand: true };
    });
  };

  const handleClear = () => setState(initialState);

  const handleToggleSign = () => {
    setState((s) => {
      if (s.display === '0' || s.display === 'Error') return s;
      const val = parseFloat(s.display) * -1;
      return { ...s, display: String(val) };
    });
  };

  const handlePercent = () => {
    setState((s) => {
      const val = parseFloat(s.display) / 100;
      return { ...s, display: String(val) };
    });
  };

  const handleBackspace = () => {
    setState((s) => {
      if (s.waitingForOperand || s.display === 'Error') return s;
      if (s.display.length === 1 || (s.display.length === 2 && s.display.startsWith('-'))) {
        return { ...s, display: '0' };
      }
      return { ...s, display: s.display.slice(0, -1) };
    });
  };

  const displayValue = formatDisplay(state.display);
  const fontSize =
    displayValue.length > 10
      ? 'text-3xl'
      : displayValue.length > 7
      ? 'text-4xl'
      : 'text-5xl';

  type BtnVariant = 'function' | 'operator' | 'equals' | 'digit';

  const btnBase =
    'flex items-center justify-center rounded-2xl text-xl font-semibold cursor-pointer select-none transition-all duration-100 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-purple-400 h-16';

  const variants: Record<BtnVariant, string> = {
    function: 'bg-zinc-700 hover:bg-zinc-600 text-white',
    operator: 'bg-purple-600 hover:bg-purple-500 text-white',
    equals: 'bg-purple-500 hover:bg-purple-400 text-white',
    digit: 'bg-zinc-800 hover:bg-zinc-700 text-white',
  };

  const Btn = ({
    label,
    onClick,
    variant = 'digit',
    wide = false,
    active = false,
  }: {
    label: string;
    onClick: () => void;
    variant?: BtnVariant;
    wide?: boolean;
    active?: boolean;
  }) => (
    <button
      onClick={onClick}
      className={cn(
        btnBase,
        variants[variant],
        wide && 'col-span-2',
        active && 'ring-2 ring-white/40'
      )}
      aria-label={label}
    >
      {label}
    </button>
  );

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-zinc-950 via-zinc-900 to-purple-950 px-4">
      <div className="w-full max-w-xs">
        {/* Title */}
        <div className="text-center mb-6">
          <span className="text-2xl font-bold tracking-tight text-white">Calcly</span>
        </div>

        {/* Calculator shell */}
        <div className="rounded-3xl bg-zinc-900/80 backdrop-blur-md border border-white/10 shadow-2xl p-5">
          {/* Display */}
          <div className="mb-4 rounded-xl bg-zinc-950 px-5 py-4 min-h-[90px] flex flex-col items-end justify-end overflow-hidden">
            {state.op && (
              <div className="text-purple-400 text-sm mb-1 font-medium">
                {state.prev} {state.op}
              </div>
            )}
            <div className={cn('font-light text-white tracking-tight leading-none', fontSize)}>
              {displayValue}
            </div>
          </div>

          {/* Buttons */}
          <div className="grid grid-cols-4 gap-3">
            <Btn label="AC" onClick={handleClear} variant="function" />
            <Btn label="+/−" onClick={handleToggleSign} variant="function" />
            <Btn label="%" onClick={handlePercent} variant="function" />
            <Btn label="÷" onClick={() => handleOperator('÷')} variant="operator" active={state.op === '÷'} />

            <Btn label="7" onClick={() => handleDigit('7')} />
            <Btn label="8" onClick={() => handleDigit('8')} />
            <Btn label="9" onClick={() => handleDigit('9')} />
            <Btn label="×" onClick={() => handleOperator('×')} variant="operator" active={state.op === '×'} />

            <Btn label="4" onClick={() => handleDigit('4')} />
            <Btn label="5" onClick={() => handleDigit('5')} />
            <Btn label="6" onClick={() => handleDigit('6')} />
            <Btn label="−" onClick={() => handleOperator('−')} variant="operator" active={state.op === '−'} />

            <Btn label="1" onClick={() => handleDigit('1')} />
            <Btn label="2" onClick={() => handleDigit('2')} />
            <Btn label="3" onClick={() => handleDigit('3')} />
            <Btn label="+" onClick={() => handleOperator('+')} variant="operator" active={state.op === '+'} />

            <Btn label="⌫" onClick={handleBackspace} />
            <Btn label="0" onClick={() => handleDigit('0')} />
            <Btn label="." onClick={() => handleDigit('.')} />
            <Btn label="=" onClick={handleEquals} variant="equals" />
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-zinc-600 mt-4">
          Vibed with{' '}
          <a href="https://marlowe.shakespeare.wtf" className="hover:text-zinc-400 underline underline-offset-2 transition-colors">
            Marlowe
          </a>
        </p>
      </div>
    </div>
  );
};

export default Index;
